/**
 * AI Code Governance - Pull Request Decisions
 *
 * Route: /code-governance/pull-requests | Nav: Code > AI Code Governance > PRs
 *
 * List of all PR evaluations with inline expandable detail:
 * - Decision badge (BLOCK/WARN/PASS)
 * - Findings grouped by severity
 * - Recommended fixes, control mappings, proof status
 *
 * Migrated onto the List & Grid Standard — see `src/pages/ApiKeysPage.tsx` (reference
 * migration) and `.specify/journals/list-grid-adoption-journal.md` (recipe). This list is
 * read-only (chevron toggles inline row expansion only — no per-row mutation), so there is
 * no Actions column and no role gating.
 */
import { Fragment, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Body1,
  Body2,
  Caption1,
  Badge,
  tokens,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import {
  ChevronDownRegular,
  ChevronRightRegular,
  CheckmarkCircleRegular,
  DocumentRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  headerWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  expandedDetail: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },
  findingGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalS,
  },
  findingItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
  },
  proofRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  proofRowBordered: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalS,
  },
  controlChipRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalXXS,
  },
  clickableRow: {
    cursor: 'pointer',
  },
  prCell: {
    display: 'flex',
    flexDirection: 'column',
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

type Decision = 'BLOCK' | 'WARN' | 'PASS';
type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

interface Finding {
  id: string;
  severity: Severity;
  policyKey: string;
  title: string;
  description: string;
  recommendedFix: string;
  controls: string[];
  frameworks: string[];
}

interface PRDecision {
  id: string;
  decision: Decision;
  repo: string;
  prNumber: number;
  prTitle: string;
  author: string;
  filesChanged: number;
  findingsCount: number;
  score: number;
  evaluatedAt: string;
  findings: Finding[];
  proofStatus: 'generated' | 'pending';
}

const demoPRs: PRDecision[] = [
  {
    id: 'pr1',
    decision: 'BLOCK',
    repo: 'leftshield/platform-api',
    prNumber: 482,
    prTitle: 'feat: add Stripe payment integration',
    author: 'dev-sarah',
    filesChanged: 8,
    findingsCount: 3,
    score: 22,
    evaluatedAt: '2026-03-18T09:14:00Z',
    proofStatus: 'generated',
    findings: [
      {
        id: 'f1',
        severity: 'CRITICAL',
        policyKey: 'AI-SEC-001',
        title: 'Hardcoded Stripe secret key in PaymentService.cs',
        description: 'Production API key sk_live_... found in source code. If merged, this key would be exposed in the repository history permanently.',
        recommendedFix: 'Move the key to Azure Key Vault and reference via configuration. Use IConfiguration injection pattern.',
        controls: ['AC-6', 'SC-12'],
        frameworks: ['SOC 2', 'PCI DSS'],
      },
      {
        id: 'f2',
        severity: 'HIGH',
        policyKey: 'AI-APP-001',
        title: 'Unvalidated payment amount from request body',
        description: 'The payment amount is read directly from the HTTP request without validation. A negative or extreme value could cause financial loss.',
        recommendedFix: 'Add FluentValidation rule: amount must be > 0 and <= configured maximum.',
        controls: ['SI-10'],
        frameworks: ['SOC 2', 'PCI DSS'],
      },
      {
        id: 'f3',
        severity: 'HIGH',
        policyKey: 'AI-DATA-001',
        title: 'Card number logged in structured output',
        description: 'The card last-four digits and expiry are included in the log context. This would violate PCI DSS requirement 3.4.',
        recommendedFix: 'Remove card details from log context. Use a payment reference ID instead.',
        controls: ['AU-3', 'SC-28'],
        frameworks: ['PCI DSS'],
      },
    ],
  },
  {
    id: 'pr2',
    decision: 'BLOCK',
    repo: 'leftshield/portal',
    prNumber: 311,
    prTitle: 'fix: admin dashboard auth flow',
    author: 'dev-james',
    filesChanged: 4,
    findingsCount: 2,
    score: 35,
    evaluatedAt: '2026-03-18T08:45:00Z',
    proofStatus: 'generated',
    findings: [
      {
        id: 'f4',
        severity: 'CRITICAL',
        policyKey: 'AI-AUTH-001',
        title: 'Admin route missing authorization check',
        description: 'The /admin/users endpoint handler has no role check. Any authenticated user could access admin functions, including user deletion.',
        recommendedFix: 'Add RequireRole("System.Admin") middleware to the admin route group.',
        controls: ['AC-3', 'AC-6'],
        frameworks: ['SOC 2', 'ISO 27001'],
      },
      {
        id: 'f5',
        severity: 'HIGH',
        policyKey: 'AI-APP-001',
        title: 'User ID from query string used without validation',
        description: 'The userId parameter is passed directly to the database query. An attacker could enumerate or access other users\' data.',
        recommendedFix: 'Validate that the requesting user has permission to access the target userId.',
        controls: ['AC-4', 'SI-10'],
        frameworks: ['SOC 2'],
      },
    ],
  },
  {
    id: 'pr3',
    decision: 'WARN',
    repo: 'leftshield/platform-api',
    prNumber: 479,
    prTitle: 'chore: update logging configuration',
    author: 'dev-maya',
    filesChanged: 3,
    findingsCount: 1,
    score: 72,
    evaluatedAt: '2026-03-17T16:30:00Z',
    proofStatus: 'generated',
    findings: [
      {
        id: 'f6',
        severity: 'MEDIUM',
        policyKey: 'AI-GOV-001',
        title: 'AI policy annotation missing on new logger configuration',
        description: 'The logging pipeline change lacks an AI governance annotation. Without it, future reviews cannot trace the decision rationale.',
        recommendedFix: 'Add [AiGovernance("logging-config-v2")] attribute to the configuration class.',
        controls: ['CM-3'],
        frameworks: ['SOC 2'],
      },
    ],
  },
  {
    id: 'pr4',
    decision: 'PASS',
    repo: 'leftshield/portal',
    prNumber: 308,
    prTitle: 'feat: add input validation to signup form',
    author: 'dev-sarah',
    filesChanged: 5,
    findingsCount: 0,
    score: 98,
    evaluatedAt: '2026-03-17T14:22:00Z',
    proofStatus: 'generated',
    findings: [],
  },
  {
    id: 'pr5',
    decision: 'BLOCK',
    repo: 'transformr/backend-services',
    prNumber: 127,
    prTitle: 'feat: dynamic report generator',
    author: 'dev-alex',
    filesChanged: 12,
    findingsCount: 4,
    score: 15,
    evaluatedAt: '2026-03-17T11:05:00Z',
    proofStatus: 'generated',
    findings: [
      {
        id: 'f7',
        severity: 'CRITICAL',
        policyKey: 'AI-EXEC-001',
        title: 'eval() called with user-supplied template string',
        description: 'User input is passed directly to eval(). An attacker could execute arbitrary code on the server, gaining full system access.',
        recommendedFix: 'Replace eval() with a sandboxed template engine (e.g., Handlebars with strict mode).',
        controls: ['SI-3', 'SI-10'],
        frameworks: ['SOC 2', 'ISO 27001'],
      },
      {
        id: 'f8',
        severity: 'HIGH',
        policyKey: 'AI-NET-001',
        title: 'HTTP call to external API without TLS verification',
        description: 'The report data fetch uses http:// instead of https://. Data in transit would be visible to network observers.',
        recommendedFix: 'Enforce HTTPS for all external API calls. Add certificate validation.',
        controls: ['SC-8', 'SC-23'],
        frameworks: ['SOC 2', 'ISO 27001'],
      },
      {
        id: 'f9',
        severity: 'HIGH',
        policyKey: 'AI-DB-001',
        title: 'SQL query built with string concatenation',
        description: 'The report filter query concatenates user input directly into SQL. This enables SQL injection, potentially exposing all database records.',
        recommendedFix: 'Use parameterized queries via Dapper or EF Core. Never concatenate user input into SQL strings.',
        controls: ['SI-10'],
        frameworks: ['SOC 2', 'PCI DSS'],
      },
      {
        id: 'f10',
        severity: 'MEDIUM',
        policyKey: 'AI-QA-001',
        title: 'No test coverage for security-sensitive report endpoint',
        description: 'The new report generation endpoint has no unit or integration tests. Regressions in access control or input handling would go undetected.',
        recommendedFix: 'Add tests covering: valid input, invalid input, unauthorized access, and SQL injection attempts.',
        controls: ['SA-11'],
        frameworks: ['SOC 2'],
      },
    ],
  },
  {
    id: 'pr6',
    decision: 'WARN',
    repo: 'leftshield/platform-api',
    prNumber: 475,
    prTitle: 'feat: add patient record export',
    author: 'dev-james',
    filesChanged: 6,
    findingsCount: 2,
    score: 65,
    evaluatedAt: '2026-03-16T15:40:00Z',
    proofStatus: 'generated',
    findings: [
      {
        id: 'f11',
        severity: 'HIGH',
        policyKey: 'AI-COMP-001',
        title: 'Regulated data path change without compliance review',
        description: 'The export endpoint handles patient health data but lacks a compliance review annotation. Changes to regulated data paths require sign-off.',
        recommendedFix: 'Add [ComplianceReview("HIPAA")] annotation and request compliance team review.',
        controls: ['PM-11'],
        frameworks: ['HIPAA'],
      },
      {
        id: 'f12',
        severity: 'MEDIUM',
        policyKey: 'AI-QA-001',
        title: 'Missing test for data masking in export output',
        description: 'The export function does not have tests verifying PII is masked. If masking fails silently, patient data would be exposed in exports.',
        recommendedFix: 'Add unit tests asserting SSN, DOB, and address fields are masked in export output.',
        controls: ['SA-11'],
        frameworks: ['HIPAA', 'SOC 2'],
      },
    ],
  },
  {
    id: 'pr7',
    decision: 'PASS',
    repo: 'leftshield/portal',
    prNumber: 305,
    prTitle: 'refactor: extract shared form validation hooks',
    author: 'dev-maya',
    filesChanged: 7,
    findingsCount: 0,
    score: 95,
    evaluatedAt: '2026-03-16T10:18:00Z',
    proofStatus: 'generated',
    findings: [],
  },
  {
    id: 'pr8',
    decision: 'BLOCK',
    repo: 'transformr/backend-services',
    prNumber: 124,
    prTitle: 'feat: webhook retry with dynamic callback URL',
    author: 'dev-alex',
    filesChanged: 3,
    findingsCount: 2,
    score: 28,
    evaluatedAt: '2026-03-15T17:55:00Z',
    proofStatus: 'generated',
    findings: [
      {
        id: 'f13',
        severity: 'CRITICAL',
        policyKey: 'AI-NET-001',
        title: 'User-controlled callback URL without allowlist',
        description: 'The webhook retry URL is taken from the request body without validation. An attacker could redirect callbacks to a malicious server, exfiltrating webhook payloads.',
        recommendedFix: 'Validate callback URLs against a configured allowlist of trusted domains.',
        controls: ['SC-7', 'SI-10'],
        frameworks: ['SOC 2'],
      },
      {
        id: 'f14',
        severity: 'HIGH',
        policyKey: 'AI-APP-001',
        title: 'Retry count from user input without bounds',
        description: 'The retry count parameter has no upper bound. A value of 999999 would cause a denial-of-service against the webhook target.',
        recommendedFix: 'Add validation: retryCount must be between 1 and 5.',
        controls: ['SI-10'],
        frameworks: ['SOC 2'],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getDecisionColor = (decision: Decision): 'danger' | 'warning' | 'success' => {
  switch (decision) {
    case 'BLOCK': return 'danger';
    case 'WARN': return 'warning';
    case 'PASS': return 'success';
  }
};

const getSeverityColor = (severity: Severity): 'danger' | 'warning' | 'important' | 'informative' => {
  switch (severity) {
    case 'CRITICAL': return 'danger';
    case 'HIGH': return 'important';
    case 'MEDIUM': return 'warning';
    case 'LOW': return 'informative';
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 90) return tokens.colorPaletteGreenForeground1;
  if (score >= 70) return tokens.colorPaletteYellowForeground1;
  if (score >= 50) return tokens.colorPaletteRedForeground2;
  return tokens.colorPaletteRedForeground1;
};

const formatTime = (iso: string): string => new Date(iso).toLocaleString();

/** Wraps the (static demo) PR decision list as a `PagedResult` — `useAdaptiveGridData` applies
 *  search/sort/paging in memory (client mode), matching the `fetchApiKeysPage` pattern in
 *  `ApiKeysPage.tsx`. This is a read-only list (no create/mutate endpoint exists yet), so there
 *  is nothing to POST/PATCH here — only the search/sort/page contract. */
async function fetchPRDecisionsPage(params: ListQueryParams): Promise<PagedResult<PRDecision>> {
  return {
    items: demoPRs,
    totalCount: demoPRs.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const crumbs: Crumb[] = [{ label: 'Pull Request Decisions' }];

// Column count for the loading skeleton — expand, decision, repo, PR, author, files,
// findings, score, evaluated. No Actions column: this list is read-only (expand only).
const COLUMN_COUNT = 9;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CodeGovernancePullRequestsPage: FC = () => {
  const styles = useStyles();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'evaluatedAt:desc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<PRDecision>({
    queryKeyBase: ['code-governance-pull-requests'],
    fetchPage: fetchPRDecisionsPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['repo', 'prTitle', 'author'],
    sortState,
  });

  const {
    items: prs,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  const handleRowClick = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Group findings by severity for expanded detail
  const renderExpandedDetail = (pr: PRDecision) => {
    if (pr.findings.length === 0) {
      return (
        <div className={styles.expandedDetail}>
          <div className={styles.proofRow}>
            <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '20px' }} />
            <Body1 style={{ fontWeight: 500 }}>All checks passed. No findings detected.</Body1>
          </div>
          <div className={styles.proofRow}>
            <Badge appearance="filled" color="success" size="small">Proof</Badge>
            <Caption1>Compliance proof record {pr.proofStatus === 'generated' ? 'generated' : 'pending'}</Caption1>
          </div>
        </div>
      );
    }

    const severityOrder: Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
    const grouped = severityOrder
      .map((sev) => ({
        severity: sev,
        items: pr.findings.filter((f) => f.severity === sev),
      }))
      .filter((g) => g.items.length > 0);

    return (
      <div className={styles.expandedDetail}>
        {grouped.map((group) => (
          <div key={group.severity} className={styles.findingGroup}>
            <div className={styles.proofRow}>
              <Badge appearance="filled" color={getSeverityColor(group.severity)}>
                {group.severity}
              </Badge>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                {group.items.length} finding{group.items.length !== 1 ? 's' : ''}
              </Body2>
            </div>
            {group.items.map((finding) => (
              <div key={finding.id} className={styles.findingItem}>
                <div className={styles.proofRow}>
                  <Badge appearance="outline" size="small">{finding.policyKey}</Badge>
                  <Body1 style={{ fontWeight: 500 }}>{finding.title}</Body1>
                </div>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{finding.description}</Body2>
                <Body2>
                  <span style={{ fontWeight: 600 }}>Fix: </span>
                  {finding.recommendedFix}
                </Body2>
                <div className={styles.controlChipRow}>
                  {finding.controls.map((c) => (
                    <Badge key={c} appearance="outline" size="small" color="informative">{c}</Badge>
                  ))}
                  {finding.frameworks.map((fw) => (
                    <Badge key={fw} appearance="tint" size="small" color="brand">{fw}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
        <div className={styles.proofRowBordered}>
          <Badge appearance="filled" color={pr.proofStatus === 'generated' ? 'success' : 'warning'} size="small">
            Proof
          </Badge>
          <Caption1>
            Compliance proof record {pr.proofStatus === 'generated' ? 'generated and stored' : 'generation pending'}
          </Caption1>
        </div>
      </div>
    );
  };

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={COLUMN_COUNT} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load pull request decisions." onRetry={refetch} />;
  } else if (prs.length === 0) {
    listBody = (
      <EmptyState
        icon={<DocumentRegular />}
        title="No Pull Request Decisions"
        description="PR evaluations will appear here once the code governance pipeline runs against a pull request."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Pull request decisions">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{/* Expand chevron — visual affordance only, not sortable */}</TableHeaderCell>
              <TableHeaderCell>Decision</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'repo' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('repo')}
                onClick={() => toggleSort('repo')}
              >
                Repo
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'prNumber' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('prNumber')}
                onClick={() => toggleSort('prNumber')}
                style={{ width: '100%' }}
              >
                PR
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'author' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('author')}
                onClick={() => toggleSort('author')}
              >
                Author
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'filesChanged' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('filesChanged')}
                onClick={() => toggleSort('filesChanged')}
              >
                Files
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'findingsCount' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('findingsCount')}
                onClick={() => toggleSort('findingsCount')}
              >
                Findings
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'score' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('score')}
                onClick={() => toggleSort('score')}
              >
                Score
              </TableHeaderCell>
              <TableHeaderCell>Evaluated</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prs.map((item) => (
              <Fragment key={item.id}>
                <TableRow className={styles.clickableRow} onClick={() => handleRowClick(item.id)}>
                  <TableCell>
                    {expandedId === item.id
                      ? <ChevronDownRegular style={{ fontSize: '16px' }} />
                      : <ChevronRightRegular style={{ fontSize: '16px' }} />}
                  </TableCell>
                  <TableCell>
                    <Badge appearance="filled" color={getDecisionColor(item.decision)}>
                      {item.decision}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.repo}</Body2>
                  </TableCell>
                  <TableCell>
                    <div className={styles.prCell}>
                      <Body1 style={{ fontWeight: 500 }}>#{item.prNumber} {item.prTitle}</Body1>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.author}</Body2>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.filesChanged}</Body2>
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance={item.findingsCount > 0 ? 'tint' : 'outline'}
                      color={item.findingsCount > 0 ? 'danger' : 'success'}
                    >
                      {item.findingsCount}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Body2 style={{ fontWeight: 600, color: getScoreColor(item.score), fontFamily: "'JetBrains Mono', monospace" }}>
                      {item.score}
                    </Body2>
                  </TableCell>
                  <TableCell>
                    <Caption1>{formatTime(item.evaluatedAt)}</Caption1>
                  </TableCell>
                </TableRow>
                {expandedId === item.id && (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT}>{renderExpandedDetail(item)}</TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <ListPageLayout
        header={
          <div className={styles.headerWrap}>
            <MasterDetailHeader crumbs={crumbs} busy={isFetching && !isLoading} />
            <Caption1 className={styles.subtitle}>
              Every PR evaluated with enforcement outcome, findings, and proof status
            </Caption1>
          </div>
        }
        toolbar={
          <ListToolbar
            search={state.search}
            onSearchChange={setSearch}
            searchPlaceholder="Search pull requests…"
            onRefresh={refetch}
            isRefreshing={isFetching}
            testIdPrefix="code-governance-prs"
          />
        }
        footer={
          <Pager
            page={state.page}
            totalPages={totalPages}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            totalCount={totalCount}
            onPageChange={setPage}
            testId="code-governance-prs-pager"
          />
        }
      >
        {listBody}
      </ListPageLayout>
    </div>
  );
};
